import { dayKey, monthKey } from '@qaytim/shared';

/**
 * Kalendar panelining matematikasi — SOF FUQAROLIK sanasi ustida.
 *
 * Panel `Date` obyekti bilan emas, `{year, month, day}` uchligi bilan ishlaydi. Sabab `time.ts`
 * dagi bilan bir xil: brauzerning mahalliy zonasi operatorning noutbukida nima bo'lishidan
 * qat'i nazar, panelda ko'ringan katak Toshkentdagi o'sha kun bo'lishi SHART. Fuqarolik
 * arifmetikasida zona umuman qatnashmaydi, ya'ni sljish uchun joy qolmaydi.
 *
 * Zona faqat BITTA savolda kerak: "bugun qaysi kun" — va u {@link todayParts} ichida,
 * `@qaytim/shared` ning o'z helperlari orqali so'raladi.
 */
export interface CivilDate {
  year: number;
  /** 1-12 */
  month: number;
  /** 1-31 */
  day: number;
}

export interface CivilMonth {
  year: number;
  /** 1-12 */
  month: number;
}

/** Oy nomlari — panel sarlavhasi va oy katakchalari uchun. */
export const MONTH_NAMES = [
  'Yanvar',
  'Fevral',
  'Mart',
  'Aprel',
  'May',
  'Iyun',
  'Iyul',
  'Avgust',
  'Sentabr',
  'Oktabr',
  'Noyabr',
  'Dekabr',
] as const;

/**
 * Hafta DUSHANBADAN boshlanadi.
 *
 * Brauzerning o'z `<input type="date">` i OS lokalidan kelib chiqib yakshanbadan boshlashi
 * mumkin — o'zbek operatori uchun bu haftani bir kunga surib ko'rsatadi va "shanba" ustuni
 * boshqa joyda turadi. Shuning uchun ustunlar tartibi shu yerda qat'iy belgilangan.
 */
export const WEEKDAY_NAMES = ['Du', 'Se', 'Cho', 'Pa', 'Ju', 'Sha', 'Ya'] as const;

const pad = (value: number, width = 2): string => String(value).padStart(width, '0');

/** `{year, month, day}` → `YYYY-MM-DD`. */
export function toDayKey({ year, month, day }: CivilDate): string {
  return `${pad(year, 4)}-${pad(month)}-${pad(day)}`;
}

/** `{year, month}` → `YYYY-MM`. */
export function toMonthKey({ year, month }: CivilMonth): string {
  return `${pad(year, 4)}-${pad(month)}`;
}

/**
 * `YYYY-MM-DD` → uchlik, yoki `null`.
 *
 * Shakl to'g'ri bo'lishi qiymat to'g'ri degani emas: `2026-02-31` naqshga mos keladi. Shuning
 * uchun kun oyning haqiqiy uzunligiga solishtiriladi — aks holda panel mavjud bo'lmagan
 * katakni "tanlangan" deb belgilab turardi.
 */
export function parseDayKey(value: string): CivilDate | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12) return null;
  if (day < 1 || day > daysInMonth(year, month)) return null;

  return { year, month, day };
}

/** `YYYY-MM` → juftlik, yoki `null`. */
export function parseMonthKey(value: string): CivilMonth | null {
  const match = /^(\d{4})-(\d{2})$/.exec(value);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  if (month < 1 || month > 12) return null;

  return { year, month };
}

/**
 * `YYYY-MM-DDTHH:mm` → sana va soat, yoki `null`.
 *
 * Soat maydoni panelda alohida turadi, lekin qiymat bitta satr bo'lib yuriydi — chunki forma
 * uni `tashkentLocalInputToIso` ga aynan shu ko'rinishda beradi.
 */
export function parseDateTimeValue(
  value: string,
): { date: CivilDate; hour: number; minute: number } | null {
  const match = /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})$/.exec(value);
  if (!match) return null;

  const date = parseDayKey(match[1] as string);
  if (!date) return null;

  const hour = Number(match[2]);
  const minute = Number(match[3]);
  if (hour > 23 || minute > 59) return null;

  return { date, hour, minute };
}

/** `YYYY-MM-DDTHH:mm` yasaydi — `datetime-local` maydoni kutgan shakl. */
export function toDateTimeValue(date: CivilDate, hour: number, minute: number): string {
  return `${toDayKey(date)}T${pad(hour)}:${pad(minute)}`;
}

/** Oydagi kunlar soni — kabisa yili `Date.UTC` orqali, qo'lda hisoblanmaydi. */
export function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/**
 * Kunning hafta kuni: 1 = dushanba … 7 = yakshanba.
 *
 * `Date.UTC` ATAYLAB — u sof fuqarolik hisobi, mahalliy zonaga umuman tegmaydi.
 */
export function isoWeekday({ year, month, day }: CivilDate): number {
  const weekday = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
  return weekday === 0 ? 7 : weekday;
}

/** Oyni `delta` ga suradi, yilni o'zi ko'chiradi. */
export function addMonths({ year, month }: CivilMonth, delta: number): CivilMonth {
  const zeroBased = year * 12 + (month - 1) + delta;
  return { year: Math.floor(zeroBased / 12), month: (zeroBased % 12) + 1 };
}

export interface CalendarCell extends CivilDate {
  /** `false` — qo'shni oyning kuni: ko'rinadi, lekin so'nik. */
  inMonth: boolean;
}

/**
 * Oyning to'liq katakchalari: 6 ta hafta × 7 kun, har doim.
 *
 * Qo'shni oy kunlari YASHIRILMAYDI, so'niq holda chiziladi — operator haftani butun ko'rishi
 * kerak ("dushanbadan yakshanbagacha" degan savol oy chegarasida ham javobsiz qolmasin).
 * Qatorlar soni doim oltita: aks holda oy almashganda panelning bo'yi sakrab, ostidagi tugmalar
 * sichqoncha tagidan siljib ketardi.
 */
export function monthGrid({ year, month }: CivilMonth): CalendarCell[] {
  const leading = isoWeekday({ year, month, day: 1 }) - 1;
  const previous = addMonths({ year, month }, -1);
  const previousLength = daysInMonth(previous.year, previous.month);
  const currentLength = daysInMonth(year, month);
  const next = addMonths({ year, month }, 1);

  const cells: CalendarCell[] = [];

  for (let index = leading; index > 0; index -= 1) {
    cells.push({ ...previous, day: previousLength - index + 1, inMonth: false });
  }
  for (let day = 1; day <= currentLength; day += 1) {
    cells.push({ year, month, day, inMonth: true });
  }
  for (let day = 1; cells.length < 42; day += 1) {
    cells.push({ ...next, day, inMonth: false });
  }

  return cells;
}

/**
 * Toshkentdagi bugun.
 *
 * Panelning "bugun" belgisi shu yerdan keladi, `getDate()` dan emas: uzoqdan ulangan yoki soati
 * noto'g'ri qo'yilgan noutbukda ikkalasi boshqa kunni ko'rsatadi, va operator efir sanasini bir
 * kun narida belgilab qo'yardi.
 *
 * Vaqt `Date.now()` orqali olinadi (klient kodidagi qolgan joylar bilan bir xil): argumentsiz
 * `new Date()` linter tomonidan taqiqlangan, va bu qulay chiqdi — `Date.now()` ni test
 * `vi.setSystemTime` bilan siljita oladi, ya'ni zona chegarasi sinovdan o'tadi.
 */
export function todayParts(): CivilDate {
  return parseDayKey(dayKey(new Date(Date.now()))) as CivilDate;
}

/** Toshkentdagi joriy oy — panel qiymatsiz ochilganda shu oydan boshlanadi. */
export function currentMonth(): CivilMonth {
  return parseMonthKey(monthKey(new Date(Date.now()))) as CivilMonth;
}

export function isSameDay(a: CivilDate, b: CivilDate): boolean {
  return a.year === b.year && a.month === b.month && a.day === b.day;
}

/** `2026-08-09` → `9-avgust, 2026` — panel ostidagi "nima yuboriladi" satri uchun. */
export function formatDayLong({ year, month, day }: CivilDate): string {
  return `${day}-${(MONTH_NAMES[month - 1] as string).toLowerCase()}, ${year}`;
}

/** `2026-08` → `Avgust 2026`. */
export function formatMonthLong({ year, month }: CivilMonth): string {
  return `${MONTH_NAMES[month - 1] as string} ${year}`;
}
